import React, { forwardRef } from 'react';
import result from 'lodash/result';

import TableContainer from './components/TableContainer/TableContainer';
import Table from './components/Table/Table';
import TableHead from './components/TableHead/TableHead';
import TableBody from './components/TableBody/TableBody';
import TableRow from './components/TableRow/TableRow';
import TableCell from './components/TableCell/TableCell';
import CheckBox from 'common/CheckBoxGrp/CheckBoxGrp';
import SortList from 'common/SortList/SortList';
import NestedRow from './NestedRow';

/**
 * TableListing is used for generating tables
 * @param {Array} columns -  [
 *  {
 * header: <String> - Name of Header
 * accessor: <Path> - Data key for the columns
 * headerCellProps: <Object> - Props for Header Cell
 * bodyCellProps: <Object> - Props for Body Cell
 * formatter: <Function> - Formatter function that will apply to the value of column for all rows
 *  }]
 * @param {Array} data
 * @param {Object} childProps - Props that will be passed to childrens -
 * {
 * container - Props for TableContainer
 * head - Props for TableHead
 * body - Props for TableBody
 * headRow - Props for TableRow where variant is 'head'
 * bodyRow - Props for TableRow where variant is 'row'
 * table - Props for Table
 * }
 * @param {Boolean} isApiError - To indicate whether there is an Api Error
 * @param {Boolean} isLoading - To indicate whether table is in loading state
 * @param {Component} Loader - Component to show when isLoading is true
 * @param {Component} NoData - Component to show when no data is available
 * @param {Component} ApiError- Component tot show when isApiError is true
 * @param {Number} rowHt - Height of single row
 * @param {Number} pageCount - No of rows visble at once
 * @param {Function} handleCheckBox - Handler for checkbox
 * @param {Function} handleSorting - Handler for sorting
 * @param {Boolean} disableSort - Disable Sorting Columns
 * @param {Boolean} reverseSort - reverse the sort
 * @param {String} [activeSortKey] - When provided, enables controlled sort mode and identifies which column reflects the current sort direction.
 * @param {Boolean} [controlledSort=false] - Explicit opt-in for controlled SortList behavior. Auto-enabled when `activeSortKey` is provided.
 * @param {Object} selectedRecords - map of selected records
 * @param {String} idKey ="id" - unique id of the table
 * @param {React.ComponentType<{ services: object[], idKey?: string, parentRow?: object }>} [renderNestedChildren] - When set with childKey, the entire nested children block is rendered inside a single colspan cell using this component, instead of mirroring all parent columns per child.
 *
 * @example
 * const data = [
 * {
 *  name: John,
 *  employer: MMT,
 *  compData: {
 *   exp: 2
 *  }
 * },
 * {
 *  name: Matthew,
 *  employer: GI,
 *  compData: {
 *   exp: 4
 *  }
 * },
 * ]
 *
 * const columns = [
 * {
 *  header: 'Employee Name'
 *  accessor: 'name'
 *  },
 * {
 *  header: 'Company Name'
 *  accessor: 'employer'
 *  },
 * {
 *  header: 'Experience'
 *  accessor: 'compData.exp'
 *  },
 * ]
 *
 * const childProps = {
 *  table: {
 *  stickyHeader: true
 *  classes: {
 *   root: 'rootClass',
 *   stickyHeaderClass: 'stickyHeaderClass'
 *   }
 *  }
 * }
 * <TableListing data={data} columns={columns}/>
 */

const TableListing = ({
	columns = [],
	data = [],
	childProps = {},
	Loader = null,
	NoData = null,
	ApiError = null,
	pageCount,
	rowHt,
	forwardedRef,
	isAllSelected,
	isApiError,
	isLoading,
	handleCheckBox,
	handleSorting,
	disableSort,
	selectedRecords,
	idKey = 'id',
	reverseSort = false,
	activeSortKey,
	controlledSort = false,
	childKey,
	renderNestedChildren
}) => {
	const isControlled = controlledSort || activeSortKey !== undefined;
	const { container = {}, head = {}, body = {}, headRow = {}, bodyRow = {}, bodyRowChild = {}, table = {} } = childProps;
	const handleCheckBoxChange = (isAllSelected, data) => (e) => {
		handleCheckBox && handleCheckBox({ checked: e.target.checked, isAllSelected, data });
	};

	const handleSortingChange = (item, sortState) => {
		handleSorting && handleSorting(item, sortState);
	};
	const noData = !(data && data.length);
	return (
		<TableContainer data-testid='table-listing' {...container} ref={forwardedRef} rowHt={rowHt} pageCount={pageCount}>
			<Table {...table}>
				<TableHead {...head}>
					<TableRow {...headRow}>
						{columns.map((column) => {
							const { header, key, headerCellProps = {}, checkBoxColumn = false, isSort } = column;
							const isDisable = isApiError || isLoading || noData;
							return (
								<TableCell key={key || header} disabled={isDisable} {...headerCellProps}>
									{checkBoxColumn ? (
										<CheckBox item='' isChecked={isAllSelected} handleChange={handleCheckBoxChange(true, {})} />
									) : isSort ? (
										<SortList
											isDisabled={disableSort}
											label={header}
											handleSort={(sortState) => handleSortingChange(column, sortState)}
											controlled={isControlled}
											reverse={isControlled ? (activeSortKey === key ? reverseSort : false) : reverseSort}
										/>
									) : header}
								</TableCell>
							);
						})}
					</TableRow>
				</TableHead>
				{!noData && (
					<TableBody {...body}>
						{data.map((item, index) => {
							if (childKey && item[childKey]?.length > 0) {
								return (
									<NestedRow
										rowData={item} key={item[idKey]} handleCheckBoxChange={handleCheckBoxChange} columns={columns} idKey={idKey} childKey={childKey}
										bodyRow={bodyRow} bodyRowChild={bodyRowChild} selectedRecords={selectedRecords} index={index} data={data} isOpen={item.isOpen}
										renderNestedChildren={renderNestedChildren}
									/>
								);
							}
							return (
								<React.Fragment key={index}>
									<TableRow key={item[idKey]} {...bodyRow}>
										{columns.map((column) => {
											const { accessor, bodyCellProps = {}, formatter, checkBoxColumn = false } = column;
											const value = !checkBoxColumn && result(item, accessor);
											const id = item[idKey];
											let isChecked = false;
											if (checkBoxColumn) {
												isChecked = selectedRecords ? !!selectedRecords[id] : item.checked;
											}
											return (
												<TableCell {...bodyCellProps} key={accessor}>
													{checkBoxColumn ? (
														<CheckBox
															item=''
															isChecked={isChecked}
															handleChange={handleCheckBoxChange(false, item)}
														/>
													) :
														formatter ? formatter(value, item, index, column, data) :
															value}
												</TableCell>
											);
										})}
									</TableRow>
								</React.Fragment>
							);
						})}
					</TableBody>
				)}

			</Table>
			{isApiError && !isLoading && ApiError}
			{noData && !isLoading && !isApiError && NoData}
			{Loader}
		</TableContainer>
	);
};

const forwardRefComp = forwardRef((props, ref) => <TableListing {...props} forwardedRef={ref} />);

forwardRefComp.displayName = TableListing.displayName || TableListing.name;

export default forwardRefComp;
